import {useRef, useCallback, useEffect} from "react";
import cn from "classnames";
import {API} from "../../api";
import {useImages} from "../../contexts/images";
import {useSetting} from "../../contexts/setting";
import {checkIfAnimated, getSizeFromBase64, fileToDataUrl, isSvgFile} from "../../libs/bg-utils";
import {coverFitTransform} from "../../libs/utils";
import {openErrorDialog} from "../../layout/ErrorDialog";
import {createUploadGenerations} from "./uploadGeneration.ts";
import {measuredCoverFit} from "./measuredCoverFit.ts";
import {getTemplateInstanceSize} from "../../data/templateManifest.ts";
import {useBannerCrop} from "../../hooks/useBannerCrop.ts";
import {PORTRAIT, TRASH_BIN, UPLOAD_MAX_BYTES} from "../../data/constants";
import {useFeature} from "hooks/useFeature";
import "./index.css";

/**
 * Per-template custom-background upload, rendered in the template-controls row
 * of any multi-template channel. TemplateControls renders it only for a
 * detached template: a locked chain, or on a scopes channel (libs/sceneScope.ts)
 * a banner whose image mode is "own". It writes this one template's slot
 * (customBgs[id]), so the upload overrides the channel's shared background for
 * this template alone; the shared background is uploaded from the BgSelector.
 *
 * Upload-first (#528, matching #169): a raster/video pick uploads to
 * /api/v3/uploads first and customBgs[id] holds the resulting CDN URL — never
 * the raw base64 blob. A local blob: URL previews instantly while the upload is
 * in flight (pendingUploadCount blocks save meanwhile); on failure the slot
 * reverts. SVGs stay inline (vector, tiny, gradient-editable). customBgs[id] is
 * preferred over the normal background by <BgTransformer> whenever the template
 * is detached (BgTransformer/templateBg.ts), so no per-template render wiring
 * is needed.
 */
const CustomBgUpload = ({id}) => {
  const fileInputRef = useRef(null);
  // Guards the async upload against the user changing this template's slot
  // mid-flight — see uploadGeneration.ts.
  const uploadGensRef = useRef(null);
  if (!uploadGensRef.current) uploadGensRef.current = createUploadGenerations();
  const {
    customBgUploaded,
    editCustomBgs,
    editCustomBgUploaded,
    editIsBgAnimation,
    incPendingUpload,
    decPendingUpload,
  } = useImages();
  const {dataChannel, product} = useSetting();
  // The crop goes where the banner reads it: the upload must not show with
  // the crop of the image it replaces (see useBannerCrop).
  const {setCrop, dropCrop} = useBannerCrop();

  // Presence of an upload marker — set on upload, cleared on remove. Distinct
  // from the transform fields so a seeded/baked default (Livestream) doesn't
  // read as "uploaded".
  const hasCustomBg = !!customBgUploaded?.[id];
  // Role feature "banner.own-bg-upload" (data/features.ts): without it the
  // banner has no upload button, but keeps the remove button of its image.
  const canUpload = useFeature("banner.own-bg-upload");

  // Cover-fit against this template's own dimensions (background-size:cover +
  // position:center). BgTransformer applies these per-id transforms.
  const coverFit = useCallback(
    (imgW, imgH) => {
      const {width, height} = getTemplateInstanceSize(dataChannel, id, product.value);
      const {scale, x, y} = coverFitTransform(imgW, imgH, width, height);
      setCrop(id, {x, y, scale, rotate: 0, flip: {x: false, y: false}});
    },
    [dataChannel, product, id, setCrop]
  );

  const handleFile = useCallback(
    (file) => {
      if (!file) return;
      const svg = isSvgFile(file);
      if (!svg && !(file.type?.startsWith("image/") || file.type?.startsWith("video/"))) return;

      if (file.size > UPLOAD_MAX_BYTES) {
        const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
        const maxMB = UPLOAD_MAX_BYTES / (1024 * 1024);
        openErrorDialog({
          title: "File too large",
          summary: `File is ${sizeMB} MB (max ${maxMB} MB). Please choose a smaller file.`,
          diagnostics: {fileName: file.name, fileSize: file.size, maxSize: UPLOAD_MAX_BYTES},
        });
        return;
      }

      (async () => {
        // Everything below is async, and this template's slot stays editable
        // meanwhile. Take a generation token up front and re-check it after
        // every await: a superseded run must write nothing at all, not even
        // the cover-fit transform.
        const gens = uploadGensRef.current;
        const token = gens.begin(id);
        // Read the file once to measure intrinsic size + detect animation.
        // The data URL is used ONLY for measurement — it is never stored for
        // raster/video (see upload-first below). Cover-fit BEFORE wiring the
        // URL so BgTransformer mounts positioned.
        const dataUrl = await fileToDataUrl(file);
        if (typeof dataUrl !== "string") return;
        if (!gens.isCurrent(id, token)) return;
        // The measurement is itself an await, so the currency re-check has to
        // come between it and the cover-fit writes — see measuredCoverFit.
        const stillCurrent = await measuredCoverFit({
          dataUrl,
          measure: getSizeFromBase64,
          isCurrent: () => gens.isCurrent(id, token),
          coverFit,
        });
        if (!stillCurrent) return;
        const isAnimated = await checkIfAnimated(dataUrl);
        if (!gens.isCurrent(id, token)) return;

        // SVGs stay inline — vector, tiny, and the gradient editor round-trips
        // the markup. They're exempt from the inline-blob ban.
        if (svg) {
          editCustomBgs(id, dataUrl, true); // locked → this template only
          editCustomBgUploaded(id, true);
          editIsBgAnimation(isAnimated);
          return;
        }

        // Raster/video: upload-first. Preview the local blob immediately, swap
        // to the CDN URL when the upload lands. pendingUploadCount blocks save
        // while a blob: URL is showing (it wouldn't survive a tab close).
        const localUrl = URL.createObjectURL(file);
        editCustomBgs(id, localUrl, true);
        editCustomBgUploaded(id, true);
        editIsBgAnimation(isAnimated);
        incPendingUpload();
        try {
          const result = await API.uploadFile(file);
          if (!result?.url) {
            // 200 with no url is a contract violation — fail fast rather than
            // silently leave the template with a dead blob: URL.
            throw new Error("Upload API returned no URL");
          }
          // Superseded while the request was in flight — the file the user is
          // actually looking at is a newer pick (or none, after Remove/unlock).
          // Drop the result; the uploaded file just stays in the gallery.
          if (!gens.isCurrent(id, token)) return;
          editCustomBgs(id, result.url, true);
        } catch (err) {
          console.error("[CustomBgUpload] upload failed", err);
          // Same check on the failure side, and it matters more: reverting here
          // would clear a newer pick's URL and transform, and the dialog would
          // report a file the user has already replaced or removed.
          if (!gens.isCurrent(id, token)) return;
          // Revert this template's slot (mirrors handleRemove).
          editCustomBgs(id, undefined, true);
          editCustomBgUploaded(id, false);
          dropCrop(id);
          editIsBgAnimation(false);
          openErrorDialog({
            title: "Couldn't upload background",
            summary:
              err?.envelope?.error?.isLikelyCors || err?.name === "NetworkError"
                ? "The upload didn't reach the server. Check your connection and try again."
                : err?.status === 413
                  ? `The file is too large (max ${UPLOAD_MAX_BYTES / (1024 * 1024)} MB). Try a smaller file.`
                  : err?.status === 400 || err?.status === 415
                    ? "Unsupported file type. Use PNG, JPEG, WebP, GIF, MP4, WebM, or MOV."
                    : `Upload failed${err?.message ? `: ${err.message}` : ""}.`,
            diagnostics: err?.envelope,
          });
        } finally {
          URL.revokeObjectURL(localUrl);
          decPendingUpload();
        }
      })();
    },
    [
      id,
      coverFit,
      editCustomBgs,
      editCustomBgUploaded,
      editIsBgAnimation,
      dropCrop,
      incPendingUpload,
      decPendingUpload,
    ]
  );

  // Unmount means the template was re-attached (unlocked, or its image reset on
  // a scopes channel), and the per-template slot is cleared when it is (by
  // TemplateControls, or by useSceneScope) — so a completion arriving after
  // that must not write either. Navigation away is unmount too, and there the
  // writes are moot.
  useEffect(() => {
    const gens = uploadGensRef.current;
    return () => gens.invalidate(id);
  }, [id]);

  const handleClick = () => fileInputRef.current?.click();
  const handleChange = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    // An upload still in flight would otherwise land afterwards and resurrect
    // the slot the user just emptied.
    uploadGensRef.current.invalidate(id);
    // Drop this template's upload + transform + flag; the template falls back
    // to the channel's normal background (shared customBg/customBgs.main, a
    // baked default, or the gallery bg).
    editCustomBgs(id, undefined, true);
    editCustomBgUploaded(id, false);
    dropCrop(id);
    editIsBgAnimation(false);
  };

  const tooltip = hasCustomBg
    ? "Replace this template's custom background"
    : "Upload a custom background for this template only";

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,video/mp4,video/webm,video/quicktime"
        onChange={handleChange}
        hidden
      />
      {canUpload && (
        <button
          type="button"
          className={cn("checkbox custom-bg", {active: hasCustomBg})}
          data-tooltip={tooltip}
          onClick={handleClick}
          aria-label={tooltip}
        >
          <img src={PORTRAIT} alt="" />
        </button>
      )}
      {hasCustomBg && (
        <button
          type="button"
          className="checkbox custom-bg-remove"
          data-tooltip="Remove this template's custom background"
          onClick={handleRemove}
          aria-label="Remove this template's custom background"
        >
          <img src={TRASH_BIN} alt="" />
        </button>
      )}
    </>
  );
};

export default CustomBgUpload;
